// Adjectives — agreement, position, comparison, possessives, plus descriptive
// and color vocabulary tables. Ported from the legacy views.adjectives;
// VOCABULARY is imported directly (old shape: { es, en, cat }).
import { VOCABULARY } from "../data/index.js";
import { renderGuideSections } from "../ui/guideSections.js";

export function adjectivesView(app) {
  const adjs = VOCABULARY.filter((v) => v.cat === "adj");
  const colors = VOCABULARY.filter((v) => v.cat === "color");

  const tables = `
    <div class="grid sm:grid-cols-2 gap-4 mt-6">
      <div class="bg-white rounded-2xl border border-sage-100 p-5">
        <h3 class="text-lg font-bold text-sage-800 mb-3">Descriptive Adjectives</h3>
        <div class="overflow-x-auto"><table class="w-full text-sm">
          <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
            <th class="py-2 pr-3">Spanish</th><th class="py-2 px-3">English</th>
          </tr></thead>
          <tbody>
            ${adjs
              .map(
                (a) => `<tr class="border-b border-sage-50 last:border-0">
              <td class="py-2 pr-3 font-medium text-sage-800">${a.es}</td>
              <td class="py-2 px-3 text-sage-700">${a.en}</td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table></div>
      </div>
      <div class="bg-white rounded-2xl border border-sage-100 p-5">
        <h3 class="text-lg font-bold text-sage-800 mb-3">Colors</h3>
        <div class="overflow-x-auto"><table class="w-full text-sm">
          <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
            <th class="py-2 pr-3">Spanish</th><th class="py-2 px-3">English</th>
          </tr></thead>
          <tbody>
            ${colors
              .map(
                (c) => `<tr class="border-b border-sage-50 last:border-0">
              <td class="py-2 pr-3 font-medium text-sage-800">${c.es}</td>
              <td class="py-2 px-3 text-sage-700">${c.en}</td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table></div>
      </div>
    </div>
  `;

  renderGuideSections(
    app,
    "Adjectives",
    "Adjectives describe nouns — and in Spanish, they have to match the noun in gender and number. They also usually come after the noun, not before.",
    [
      {
        title: "1. Agreement — gender & number",
        body: `
          <p class="text-sage-700 mb-3">Adjectives change their ending to match the noun they describe.</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-3">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Type</th><th class="py-2 px-3">M. sg</th><th class="py-2 px-3">F. sg</th><th class="py-2 px-3">M. pl</th><th class="py-2 px-3">F. pl</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">-o / -a</td><td class="py-2 px-3">alto</td><td class="py-2 px-3">alta</td><td class="py-2 px-3">altos</td><td class="py-2 px-3">altas</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Vowel (one form)</td><td class="py-2 px-3">grande</td><td class="py-2 px-3">grande</td><td class="py-2 px-3">grandes</td><td class="py-2 px-3">grandes</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Consonant</td><td class="py-2 px-3">feliz</td><td class="py-2 px-3">feliz</td><td class="py-2 px-3">felices</td><td class="py-2 px-3">felices</td></tr>
              <tr><td class="py-2 pr-3 font-medium">Nationalities</td><td class="py-2 px-3">español</td><td class="py-2 px-3">española</td><td class="py-2 px-3">españoles</td><td class="py-2 px-3">españolas</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm">Examples: <i>el coche rojo, la casa roja, los coches rojos, las casas rojas</i>.</p>
        `,
      },
      {
        title: "2. Position — after the noun (usually)",
        body: `
          <p class="text-sage-700 mb-2">Default position is <b>after</b> the noun: <i>una casa grande</i>, <i>un libro interesante</i>.</p>
          <p class="text-sage-700 mb-2">Some adjectives change meaning depending on position:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>un <b>gran</b> hombre</i> = a great man · <i>un hombre <b>grande</b></i> = a big man</li>
            <li><i>un <b>viejo</b> amigo</i> = an old (long-time) friend · <i>un amigo <b>viejo</b></i> = an elderly friend</li>
            <li><i>mi <b>pobre</b> hermano</i> = my poor (pitied) brother · <i>mi hermano <b>pobre</b></i> = my poor (broke) brother</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>Shortened forms before masculine singular nouns:</b> bueno→buen, malo→mal, primero→primer, tercero→tercer, grande→gran (before any singular noun).</p>
        `,
      },
      {
        title: "3. Comparison",
        body: `
          <ul class="text-sage-700 text-sm space-y-2 list-disc pl-5">
            <li><b>More than:</b> <i>más + adj + que</i> → <i>María es <b>más alta que</b> Juan.</i></li>
            <li><b>Less than:</b> <i>menos + adj + que</i> → <i>El libro es <b>menos caro que</b> el otro.</i></li>
            <li><b>As ... as:</b> <i>tan + adj + como</i> → <i>Es <b>tan rápido como</b> tú.</i></li>
            <li><b>Superlative:</b> <i>el/la/los/las más + adj</i> → <i>la más bonita</i>.</li>
            <li><b>Irregulars:</b> bueno → <b>mejor</b>, malo → <b>peor</b>, grande → <b>mayor</b>, pequeño → <b>menor</b>.</li>
            <li><b>Absolute superlative (-ísimo):</b> <i>grande → grandísimo, fácil → facilísimo</i>.</li>
          </ul>
        `,
      },
      {
        title: "4. Possessive adjectives",
        body: `
          <div class="overflow-x-auto"><table class="w-full text-sm">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Owner</th><th class="py-2 px-3">Singular</th><th class="py-2 px-3">Plural</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">my</td><td class="py-2 px-3">mi</td><td class="py-2 px-3">mis</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">your (tú)</td><td class="py-2 px-3">tu</td><td class="py-2 px-3">tus</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">his/her/its/your (ud.)</td><td class="py-2 px-3">su</td><td class="py-2 px-3">sus</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">our</td><td class="py-2 px-3">nuestro/a</td><td class="py-2 px-3">nuestros/as</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">your (vosotros)</td><td class="py-2 px-3">vuestro/a</td><td class="py-2 px-3">vuestros/as</td></tr>
              <tr><td class="py-2 pr-3 font-medium">their/your (uds.)</td><td class="py-2 px-3">su</td><td class="py-2 px-3">sus</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm mt-3">Agreement is with the <b>thing owned</b>, not the owner: <i>nuestra casa</i> (our house — house is feminine), <i>nuestros libros</i> (our books — books are masculine plural).</p>
        `,
      },
    ],
    "",
    tables
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
